import type { HolisticSummary, HolisticSummaryCategory } from '@fruitful/types';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { requestBuilder } from '../../http/RequestBuilder';
import {
    holisticSummaryAtom,
    holisticSummaryCacheAtom,
    isLoadingHolisticSummaryAtom,
} from '../state/HolisticAtoms';
import type { HolisticSummaryCache } from '../types/HolisticSummary';

const HOLISTIC_SUMMARY_STALE_TIME = 60 * 1000 * 5; // 5 minutes

/**
 * Resets a holistic summary by clearing all accounts and setting balances to null for each category.
 * This is useful when you want to show a placeholder state while fetching fresh data.
 *
 * @param holisticSummary - The holistic summary to reset
 * @returns A new holistic summary with all categories reset to empty state
 */
const getHolisticSummaryPlaceholder = (
    holisticSummary: HolisticSummary,
): HolisticSummary => {
    const resetSummary: HolisticSummary = { ...holisticSummary };

    for (const category in resetSummary) {
        const originalData =
            holisticSummary[category as HolisticSummaryCategory];

        resetSummary[category as HolisticSummaryCategory] = {
            accounts: originalData.accounts.map((a) => ({
                ...a,
                // clear out anything we don't want in local storage
                currentBalanceCents: undefined,
                accountNumberMasked: undefined,
                name: '',
            })),
            totalCurrentBalanceCents: null,
        };
    }

    return resetSummary;
};

interface QueryDataCache {
    placeholderData: HolisticSummary | undefined;
    initialData: HolisticSummary | undefined;
    initialDataUpdatedAt: number | undefined;
}

const getQueryDataFromCache = (
    holisticSummaryCache: HolisticSummaryCache | null,
    userId: string,
): QueryDataCache => {
    // We're checking the metadata.userId here to ensure that the cached data is for the current user
    // In Production, we would not expect the cached data to be for a different user (that would be
    // members of different memberships sharing a device/browser). But since the data is especially
    // sensitive and since the scenario for development and testing is common, it is worth handling
    if (
        holisticSummaryCache?.holisticSummary == null ||
        holisticSummaryCache.userId !== userId
    ) {
        return {
            placeholderData: undefined,
            initialData: undefined,
            initialDataUpdatedAt: undefined,
        };
    }

    const { holisticSummary, dataUpdatedAt = 0 } = holisticSummaryCache;

    // We want to treat this cached data differently if it is "stale" on initial load:
    // - If it is _not_ stale, we can just show the data as-is as `initialData`, and gain the performance benefits of the cache
    // - If it _is_ stale, we can show the data as `placeholderData`, which can be used for a loading state UI while fetching
    // fresh data, but we want to first remove potentially inaccurate numbers.
    const isStale = true; // TODO consider if we actually want to cache real data. If so use `new Date().getTime() - dataUpdatedAt > HOLISTIC_SUMMARY_STALE_TIME;`

    if (isStale) {
        return {
            placeholderData: getHolisticSummaryPlaceholder(holisticSummary),
            initialData: undefined,
            initialDataUpdatedAt: undefined,
        };
    }

    return {
        placeholderData: undefined,
        initialData: holisticSummary,
        initialDataUpdatedAt: dataUpdatedAt,
    };
};

const fetchHolisticData = requestBuilder.query<
    HolisticSummary,
    { userId: string }
>({
    endpoint: () => '/api/holistic/summary',
    queryKey: ({ userId }) => `getAccountOverviewSummary/${userId}`,
});

const useGetHolisticSummary = (enabled = true) => {
    const user = useAuthedUser();

    const setHolisticSummaryAtom = useSetAtom(holisticSummaryAtom);
    const setIsLoadingAtom = useSetAtom(isLoadingHolisticSummaryAtom);
    const setHolisticSummaryCacheAtom = useSetAtom(holisticSummaryCacheAtom);

    // We likely have a cache of the holistic summary in local storage, which will be in holisticSummaryCacheAtom on load.
    // We don't need the logic for this cached data to run every time the hook is called -- only on initial load.
    const isInitialLoad = useAtomValue(isLoadingHolisticSummaryAtom) !== false;
    const holisticSummaryCache = useRef(
        useAtomValue(holisticSummaryCacheAtom),
    ).current;
    const queryDataCache = useMemo(() => {
        return isInitialLoad
            ? getQueryDataFromCache(holisticSummaryCache, user.id)
            : undefined;
    }, [isInitialLoad]);

    const {
        data,
        isLoading,
        refetch,
        isRefetching,
        isError,
        isStale,
        dataUpdatedAt,
        isPlaceholderData,
    } = fetchHolisticData(
        {
            userId: user.id,
        },
        {
            enabled,
            placeholderData: queryDataCache?.placeholderData,
            initialData: queryDataCache?.initialData,
            initialDataUpdatedAt: queryDataCache?.initialDataUpdatedAt,
            staleTime: HOLISTIC_SUMMARY_STALE_TIME,
        },
    );

    useEffect(() => {
        // From Tanstack Query docs: https://tanstack.com/query/latest/docs/framework/react/guides/placeholder-query-data
        // When we use placeholderData, our Query will not be in a pending state because we have data to display
        // We want isLoadingHolisticSummaryAtom to reflect the status of when non-placeholder data has been loaded,
        // so we check for that here.
        if (!isPlaceholderData) {
            setIsLoadingAtom(isLoading);
        }
    }, [isLoading, isPlaceholderData]);

    useEffect(() => {
        if (data) {
            if (!isPlaceholderData) {
                // no need to re-write placeholder data
                setHolisticSummaryCacheAtom({
                    holisticSummary: getHolisticSummaryPlaceholder(data), // TODO consider if we actually want to cache real data. If so, remove getHolisticSummaryPlaceholder().
                    dataUpdatedAt,
                    userId: user.id,
                });
            }
            // Good to pass placeholderData along here for initial loading logic/UI
            setHolisticSummaryAtom(data);
        }
    }, [data, dataUpdatedAt, isPlaceholderData]);

    return {
        data,
        isLoading: isLoading || isRefetching,
        isRefetching,
        refetch,
        isError,
        isStale,
    };
};

export default useGetHolisticSummary;
